/**
 * Hero — the top-of-page banner: eyebrow, multi-line title, subtitle, CTA
 * buttons, optional meta stats row, optional art, an optional 3D brand `mark`,
 * and an optional live `demo` (an animated product showcase, e.g. the autoplay
 * FlowDemo). With a mark OR a demo the hero becomes a split layout (text left,
 * object right) — the signature home treatment; without one it stays centered.
 * The mark floats + cursor-parallaxes; the demo sits still (it's a UI, not art).
 * Composes Button + tokens. Motion (entrance, float, parallax) is reduced-motion-safe.
 */
import { useEffect, useRef, type CSSProperties, type ReactNode } from 'react';
import { Button } from '../button';
import { MarketingLink } from '../marketing-link';
import { prefersReducedMotion } from '../../lib/motion';
import { cn } from '../../utils';
import { FlowDemo, DEMO_SCENARIO_BY_ID } from './FlowDemo';

export interface HeroCta {
  readonly label: string;
  readonly href: string;
  readonly variant?: 'default' | 'outline';
}

export interface HeroMeta {
  readonly label: string;
  readonly value: ReactNode;
}

export interface HeroProps {
  readonly eyebrow?: string;
  /** Title; "\n" renders as a line break. */
  readonly title: string;
  readonly subtitle?: ReactNode;
  readonly ctas?: readonly HeroCta[];
  readonly meta?: readonly HeroMeta[];
  /** Optional hero art (e.g. the Inferrex icon lockup). */
  readonly art?: string;
  /** Optional 3D brand object — switches the hero to the split layout. */
  readonly mark?: string;
  readonly markAlt?: string;
  /**
   * Optional demo scenario id (e.g. "self-healing") — switches the hero to the
   * split layout with the autoplay FlowDemo for that scenario in the right
   * column. Self-contained so the hero is a uniform `<Hero {...p}/>` in the
   * block registry (no demo node injected by the renderer). Takes precedence
   * over `mark`.
   */
  readonly demoId?: string;
  /** Optional photo (e.g. the founder portrait) — switches the hero to the split
   *  layout with the image in the right column, sized modestly (not full-bleed). */
  readonly image?: string;
  readonly imageAlt?: string;
  /** Intrinsic pixel dimensions of `image` / `mark` (codegen-stamped for raster
   *  sources) so the browser reserves the hero box — no CLS on the above-the-fold
   *  LCP element. Display size stays CSS-driven. */
  readonly imageWidth?: number;
  readonly imageHeight?: number;
  readonly markWidth?: number;
  readonly markHeight?: number;
}

/** `*word*` → editorial italic emphasis (Instrument Serif, accent) — deck-consistent. */
const editorialize = (line: string): string =>
  line.replace(/\*([^*]+)\*/g, '<em class="font-editorial font-normal text-primary">$1</em>');

export function Hero({ eyebrow, title, subtitle, ctas, meta, art, mark, markAlt, demoId, image, imageAlt, imageWidth, imageHeight, markWidth, markHeight }: HeroProps) {
  // Intrinsic dimensions reserve the box (CLS) on the above-the-fold hero image;
  // CSS classes still drive the displayed size.
  const imageDims = imageWidth && imageHeight ? { width: imageWidth, height: imageHeight } : {};
  const markDims = markWidth && markHeight ? { width: markWidth, height: markHeight } : {};
  const titleLines = title.split('\n');
  // Self-contained: the manifest carries a demo scenario id; the hero renders
  // the autoplay FlowDemo for it (no React node injected by the renderer).
  const scenario = demoId ? DEMO_SCENARIO_BY_ID[demoId] : undefined;
  const demo = scenario ? <FlowDemo scenario={scenario} autoplay /> : undefined;
  const hasDemo = !!demo;
  const hasImage = !!image && !hasDemo;          // a photo in the right column
  const hasMark = !!mark && !hasDemo && !hasImage; // demo/image take precedence
  const split = hasMark || hasDemo || hasImage;  // any switches to text-left layout
  const sectionRef = useRef<HTMLElement>(null);
  const parallaxRef = useRef<HTMLDivElement>(null);

  // Cursor parallax on the 3D mark only (a demo is a UI, it stays still).
  useEffect(() => {
    if (!hasMark || prefersReducedMotion()) return;
    const section = sectionRef.current;
    const layer = parallaxRef.current;
    if (!section || !layer) return;
    const onMove = (e: MouseEvent) => {
      const r = section.getBoundingClientRect();
      const dx = (e.clientX - (r.left + r.width / 2)) / r.width;
      const dy = (e.clientY - (r.top + r.height / 2)) / r.height;
      layer.style.setProperty('--px', `${(dx * 24).toFixed(1)}px`);
      layer.style.setProperty('--py', `${(dy * 24).toFixed(1)}px`);
    };
    section.addEventListener('mousemove', onMove);
    return () => section.removeEventListener('mousemove', onMove);
  }, [hasMark]);

  const content = (
    <>
      {eyebrow && (
        <p className="motion-hero-item mb-4 font-mono text-sm uppercase tracking-wider text-primary" style={{ '--reveal-index': 0 } as CSSProperties}>{eyebrow}</p>
      )}
      <h1 className="motion-hero-item font-display text-(length:--hero-title-size) font-bold leading-tight tracking-tight text-foreground" style={{ '--reveal-index': 1 } as CSSProperties}>
        {titleLines.map((line, i) => (
          <span key={i} className="block" dangerouslySetInnerHTML={{ __html: editorialize(line) }} />
        ))}
      </h1>
      {subtitle && (
        <p className={cn('motion-hero-item mt-6 max-w-2xl text-(length:--hero-subtitle-size) leading-relaxed text-muted-foreground', !split && 'mx-auto')} style={{ '--reveal-index': 2 } as CSSProperties}>{subtitle}</p>
      )}
      {ctas && ctas.length > 0 && (
        <div className={cn('motion-hero-item mt-8 flex flex-wrap items-center gap-3', split ? 'justify-start' : 'justify-center')} style={{ '--reveal-index': 3 } as CSSProperties}>
          {ctas.map((cta) => (
            <Button key={cta.href} asChild size="lg" variant={cta.variant ?? 'default'}>
              <MarketingLink href={cta.href}>{cta.label}</MarketingLink>
            </Button>
          ))}
        </div>
      )}
      {art && (
        <img src={art} alt="" aria-hidden="true" loading="eager" className={cn('motion-hero-item mt-12 w-40 max-w-full', !split && 'mx-auto')} style={{ '--reveal-index': 4 } as CSSProperties} />
      )}
      {meta && meta.length > 0 && (
        <dl className={cn('motion-hero-item mt-14 flex max-w-3xl flex-wrap items-center gap-x-6 gap-y-6 sm:gap-x-12', split ? 'justify-start' : 'mx-auto justify-center')} style={{ '--reveal-index': 4 } as CSSProperties}>
          {meta.map((m) => (
            <div key={m.label} className={split ? '' : 'text-center'}>
              <dt className="font-display text-3xl font-bold text-primary">{m.value}</dt>
              <dd className="mt-1 font-mono text-xs uppercase tracking-wider text-muted-foreground">{m.label}</dd>
            </div>
          ))}
        </dl>
      )}
    </>
  );

  return (
    <section
      ref={sectionRef}
      className={cn('relative overflow-hidden border-b border-border px-6 pb-(--hero-pad-bottom) pt-(--hero-pad-top)', split ? 'text-left' : 'text-center')}
    >
      {/* Branded hero glow — the --hero-glow gradient token (theme-aware), its own
          layer so it can bloom in on mount (.motion-glow). */}
      <div
        aria-hidden="true"
        className="motion-glow pointer-events-none absolute inset-0 -z-10"
        style={{ backgroundImage: 'var(--hero-glow)' }}
      />
      {split ? (
        <div className={cn('mx-auto grid max-w-6xl items-center gap-(--hero-gap)', hasDemo ? 'lg:grid-cols-(--hero-cols-demo)' : 'lg:grid-cols-(--hero-cols)')}>
          <div>{content}</div>
          {hasDemo ? (
            // The live demo — a UI showcase, so it sits still (enters once, no
            // float/parallax) and spans the full column width.
            <div className="motion-hero-item w-full min-w-0" style={{ '--reveal-index': 2 } as CSSProperties}>
              {demo}
            </div>
          ) : hasImage ? (
            // A photo (e.g. the founder portrait) — modest width, framed; not full-bleed.
            <div className="motion-hero-item justify-self-center lg:justify-self-end" style={{ '--reveal-index': 2 } as CSSProperties}>
              <img src={image} alt={imageAlt ?? ''} loading="eager" {...imageDims} className="w-full max-w-xs rounded-card border border-border shadow-2xl" />
            </div>
          ) : (
            <div
              ref={parallaxRef}
              className="ix-parallax motion-hero-item justify-self-center lg:justify-self-end"
              style={{ '--reveal-index': 2, transform: 'translate(var(--px,0),var(--py,0))' } as CSSProperties}
            >
              <img
                src={mark}
                alt={markAlt ?? ''}
                aria-hidden={markAlt ? undefined : true}
                loading="eager"
                {...markDims}
                className="motion-float w-(--hero-mark-size) max-w-full drop-shadow-2xl"
              />
            </div>
          )}
        </div>
      ) : (
        <div className="mx-auto max-w-5xl">{content}</div>
      )}
    </section>
  );
}
